/**
 * @fileoverview Sync Barrier for coordinating two ZodiacEngine instances
 *
 * Provides synchronization primitives to ensure both engines have processed
 * Firebase updates before proceeding with test verification.
 */

import { ZodiacEngine } from "../../zodiacEngine/zodiacEngine";
import { GameState } from "../../zodiacEngine/types";
import { SyncTiming, FIREBASE_TIMEOUTS, EngineStateSnapshot, extractStateSnapshot } from "./FirebaseTestTypes";

/**
 * Sync Barrier coordinates two engines waiting for Firebase updates
 */
export class SyncBarrier {
  private lightEngine: ZodiacEngine;
  private darkEngine: ZodiacEngine;
  private gameId: string;
  private pollInterval: number;
  private syncTimings: SyncTiming[] = [];

  constructor(
    lightEngine: ZodiacEngine,
    darkEngine: ZodiacEngine,
    gameId: string,
    pollInterval: number = 50
  ) {
    this.lightEngine = lightEngine;
    this.darkEngine = darkEngine;
    this.gameId = gameId;
    this.pollInterval = pollInterval;
  }

  /**
   * Get all sync timings recorded
   */
  getSyncTimings(): SyncTiming[] {
    return [...this.syncTimings];
  }

  /**
   * Clear sync timings
   */
  clearTimings(): void {
    this.syncTimings = [];
  }

  /**
   * Wait until both engines have processed the same number of moves
   * Uses fenHistory.length as the synchronization counter
   */
  async waitForBothEnginesAtMoveCount(
    expectedMoveCount: number,
    timeoutMs: number = FIREBASE_TIMEOUTS.SYNC_BARRIER
  ): Promise<{ lightState: GameState; darkState: GameState; syncTimeMs: number }> {
    const startTime = performance.now();
    const stepName = `sync_move_${expectedMoveCount}`;

    while (performance.now() - startTime < timeoutMs) {
      const lightState = this.lightEngine.getGameState();
      const darkState = this.darkEngine.getGameState();

      const lightMoveCount = lightState.fenHistory?.length ?? 0;
      const darkMoveCount = darkState.fenHistory?.length ?? 0;

      if (lightMoveCount >= expectedMoveCount && darkMoveCount >= expectedMoveCount) {
        const syncTimeMs = performance.now() - startTime;
        this.recordTiming(stepName, syncTimeMs, startTime);
        return { lightState, darkState, syncTimeMs };
      }

      await this.delay(this.pollInterval);
    }

    const elapsed = performance.now() - startTime;
    this.recordTiming(stepName, elapsed, startTime);
    throw new Error(
      `Sync timeout waiting for move count ${expectedMoveCount}. ` +
        `Light: ${this.lightEngine.getGameState().fenHistory?.length ?? 0}, ` +
        `Dark: ${this.darkEngine.getGameState().fenHistory?.length ?? 0}`
    );
  }

  /**
   * Wait until a specific field matches on both engines
   */
  async waitForFieldSync<T>(
    fieldName: string,
    fieldGetter: (state: GameState) => T,
    expectedValue?: T,
    timeoutMs: number = FIREBASE_TIMEOUTS.SYNC_BARRIER
  ): Promise<{ lightValue: T; darkValue: T; syncTimeMs: number }> {
    const startTime = performance.now();
    const stepName = `sync_field_${fieldName}`;

    while (performance.now() - startTime < timeoutMs) {
      const lightState = this.lightEngine.getGameState();
      const darkState = this.darkEngine.getGameState();

      const lightValue = fieldGetter(lightState);
      const darkValue = fieldGetter(darkState);

      // Check if values match each other
      const valuesMatch = JSON.stringify(lightValue) === JSON.stringify(darkValue);

      // If expected value provided, also check against it
      const matchesExpected =
        expectedValue === undefined ||
        JSON.stringify(lightValue) === JSON.stringify(expectedValue);

      if (valuesMatch && matchesExpected) {
        const syncTimeMs = performance.now() - startTime;
        this.recordTiming(stepName, syncTimeMs, startTime);
        return { lightValue, darkValue, syncTimeMs };
      }

      await this.delay(this.pollInterval);
    }

    const elapsed = performance.now() - startTime;
    this.recordTiming(stepName, elapsed, startTime);

    const lightValue = fieldGetter(this.lightEngine.getGameState());
    const darkValue = fieldGetter(this.darkEngine.getGameState());

    throw new Error(
      `Sync timeout waiting for field "${fieldName}". ` +
        `Light: ${JSON.stringify(lightValue)}, Dark: ${JSON.stringify(darkValue)}`
    );
  }

  /**
   * Wait until both engines agree on turn state
   */
  async waitForTurnSync(
    expectedIsLightTurn?: boolean,
    timeoutMs: number = FIREBASE_TIMEOUTS.SYNC_BARRIER
  ): Promise<{ isLightTurn: boolean; syncTimeMs: number }> {
    const result = await this.waitForFieldSync(
      "isLightTurn",
      (state) => state.isLightTurn,
      expectedIsLightTurn,
      timeoutMs
    );
    return { isLightTurn: result.lightValue, syncTimeMs: result.syncTimeMs };
  }

  /**
   * Wait until both engines agree on bonus turn state
   */
  async waitForBonusTurnSync(
    timeoutMs: number = FIREBASE_TIMEOUTS.SYNC_BARRIER
  ): Promise<{
    skipTurnChange: boolean;
    activePieceId: string | null;
    syncTimeMs: number;
  }> {
    const startTime = performance.now();
    const stepName = "sync_bonus_turn";

    while (performance.now() - startTime < timeoutMs) {
      const lightState = this.lightEngine.getGameState();
      const darkState = this.darkEngine.getGameState();

      const lightSkip = lightState.skipTurnChange ?? false;
      const darkSkip = darkState.skipTurnChange ?? false;
      const lightPieceId = lightState.activeBonusTurnPieceId ?? null;
      const darkPieceId = darkState.activeBonusTurnPieceId ?? null;

      // Both must agree on skipTurnChange
      // For pieceId, we just check they're both set or both null
      // (actual ID may differ due to Firebase sync regenerating IDs)
      const skipMatches = lightSkip === darkSkip;
      const pieceIdMatches =
        (lightPieceId === null && darkPieceId === null) ||
        (lightPieceId !== null && darkPieceId !== null);

      if (skipMatches && pieceIdMatches) {
        const syncTimeMs = performance.now() - startTime;
        this.recordTiming(stepName, syncTimeMs, startTime);
        return {
          skipTurnChange: lightSkip,
          activePieceId: lightPieceId,
          syncTimeMs,
        };
      }

      await this.delay(this.pollInterval);
    }

    const elapsed = performance.now() - startTime;
    this.recordTiming(stepName, elapsed, startTime);

    const lightState = this.lightEngine.getGameState();
    const darkState = this.darkEngine.getGameState();

    throw new Error(
      `Bonus turn sync timeout. ` +
        `Light: skip=${lightState.skipTurnChange}, pieceId=${lightState.activeBonusTurnPieceId}. ` +
        `Dark: skip=${darkState.skipTurnChange}, pieceId=${darkState.activeBonusTurnPieceId}`
    );
  }

  /**
   * Wait until both engines have same FEN (normalized)
   */
  async waitForFENSync(
    timeoutMs: number = FIREBASE_TIMEOUTS.SYNC_BARRIER
  ): Promise<{ fen: string; syncTimeMs: number }> {
    const { ZodiacFEN } = require("../../zodiacEngine/Fen/ZodiacFEN");
    const startTime = performance.now();
    const stepName = "sync_fen";

    while (performance.now() - startTime < timeoutMs) {
      const lightState = this.lightEngine.getGameState();
      const darkState = this.darkEngine.getGameState();

      const lightFEN = ZodiacFEN.encode(lightState);
      const darkFEN = ZodiacFEN.encode(darkState);

      // Normalize by removing turn indicator for comparison
      const lightNorm = this.normalizeFEN(lightFEN);
      const darkNorm = this.normalizeFEN(darkFEN);

      if (lightNorm === darkNorm) {
        const syncTimeMs = performance.now() - startTime;
        this.recordTiming(stepName, syncTimeMs, startTime);
        return { fen: lightFEN, syncTimeMs };
      }

      await this.delay(this.pollInterval);
    }

    const elapsed = performance.now() - startTime;
    this.recordTiming(stepName, elapsed, startTime);

    const { ZodiacFEN: FEN } = require("../../zodiacEngine/Fen/ZodiacFEN");
    const lightFEN = FEN.encode(this.lightEngine.getGameState());
    const darkFEN = FEN.encode(this.darkEngine.getGameState());

    throw new Error(`FEN sync timeout. Light: ${lightFEN}, Dark: ${darkFEN}`);
  }

  /**
   * Get current state snapshots from both engines
   */
  getStateSnapshots(): { light: EngineStateSnapshot; dark: EngineStateSnapshot } {
    return {
      light: extractStateSnapshot(this.lightEngine.getGameState()),
      dark: extractStateSnapshot(this.darkEngine.getGameState()),
    };
  }

  /**
   * Check if both engines currently agree on state
   */
  checkCurrentConsistency(): {
    consistent: boolean;
    lightSnapshot: EngineStateSnapshot;
    darkSnapshot: EngineStateSnapshot;
  } {
    const snapshots = this.getStateSnapshots();
    const lightNorm = this.normalizeFEN(snapshots.light.fen);
    const darkNorm = this.normalizeFEN(snapshots.dark.fen);

    const consistent =
      lightNorm === darkNorm &&
      snapshots.light.isLightTurn === snapshots.dark.isLightTurn &&
      snapshots.light.skipTurnChange === snapshots.dark.skipTurnChange;

    return {
      consistent,
      lightSnapshot: snapshots.light,
      darkSnapshot: snapshots.dark,
    };
  }

  /**
   * Normalize FEN for comparison
   */
  private normalizeFEN(fen: string): string {
    let normalized = fen.trim();
    // Remove turn indicator prefix
    if (normalized.startsWith("d") || normalized.startsWith("l")) {
      normalized = normalized.substring(1);
    }
    return normalized;
  }

  /**
   * Record sync timing
   */
  private recordTiming(step: string, durationMs: number, startedAt: number): void {
    this.syncTimings.push({ step, durationMs, startedAt });
  }

  /**
   * Utility delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
